import React, { useEffect } from 'react';
import { Navigate, Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import {
  ScaffolderFieldExtensions,
  createScaffolderFieldExtension,
} from '@backstage/plugin-scaffolder-react';
import { AzureTokenField } from './scaffolder/AzureTokenField';
import { MyGroupsPicker } from './scaffolder/MyGroupsPicker';
import { AzureSubscriptionPicker } from './scaffolder/AzureSubscriptionPicker';
import { AzureResourceGroupPicker } from './scaffolder/AzureResourceGroupPicker';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import {
  SettingsLayout,
  UserSettingsAuthProviders,
  UserSettingsGeneral,
  UserSettingsPage,
} from '@backstage/plugin-user-settings';
import { apis, oktaAuthApiRef } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';
import { TemplateManagementPage } from './components/templates/TemplateManagementPage';
import { GovernancePage } from './components/governance/GovernancePage';
import { DeveloperAccessHubPage } from './components/access/DeveloperAccessHubPage';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';

import {
  configApiRef,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  UnifiedThemeProvider,
  themes as backstageThemes,
} from '@backstage/theme';
import { cubeAngleTheme } from './theme/cubeAngleTheme';
import { CssBaseline } from '@material-ui/core';
import { getArtaAuthProvider } from './artaEdition';
import { ArtaTemplateListPage } from './scaffolder/ArtaTemplateListPage';

// Custom scaffolder field extension for acquiring Azure management tokens via OBO flow
const AzureTokenFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'AzureTokenField',
    component: AzureTokenField,
  }),
);

// Custom scaffolder field that shows only the signed-in user's groups
const MyGroupsPickerExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'MyGroupsPicker',
    component: MyGroupsPicker,
  }),
);

// Custom scaffolder field that lists Azure subscriptions by name
const AzureSubscriptionPickerExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'AzureSubscriptionPicker',
    component: AzureSubscriptionPicker,
  }),
);

const AzureResourceGroupPickerExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'AzureResourceGroupPicker',
    component: AzureResourceGroupPicker,
  }),
);

const app = createApp({
  apis,
  components: {
    SignInPage: props => {
      useEffect(() => {
        const titleEl = document.querySelector('title');
        if (!titleEl) return undefined;
        const deduplicateTitle = () => {
          const titleParts = document.title.split(' | ');
          if (titleParts.length === 2 && titleParts[0] === titleParts[1]) {
            document.title = titleParts[0];
          }
        };
        deduplicateTitle();
        const observer = new MutationObserver(deduplicateTitle);
        observer.observe(titleEl, { childList: true, subtree: true });
        return () => observer.disconnect();
      }, []);
      const configApi = useApi(configApiRef);
      const authProvider = getArtaAuthProvider(configApi);
      const providers = authProvider === 'microsoft'
        ? [{
            id: 'microsoft-auth-provider',
            title: '',
            message: 'Sign in to ARTA',
            apiRef: microsoftAuthApiRef,
          }]
        : [{
            id: 'okta-auth-provider',
            title: '',
            message: 'Sign in to ARTA',
            apiRef: oktaAuthApiRef,
          }];
      return (
        <SignInPage
          {...props}
          auto={false}
          providers={providers}
        />
      );
    },
  },
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  themes: [
    {
      id: 'light',
      title: 'Cubeangle',
      variant: 'dark',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={cubeAngleTheme} noCssBaseline>
          <CssBaseline>{children}</CssBaseline>
        </UnifiedThemeProvider>
      ),
    },
    {
      id: 'dark',
      title: 'Dark',
      variant: 'dark',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={backstageThemes.dark} noCssBaseline>
          <CssBaseline>{children}</CssBaseline>
        </UnifiedThemeProvider>
      ),
    },
  ],
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="catalog" />} />
    <Route path="/home" element={<Navigate to="/catalog" replace />} />
    <Route
      path="/catalog"
      element={<CatalogIndexPage initiallySelectedFilter="all" />}
    />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route
      path="/create"
      element={
        <ScaffolderPage
          components={{
            EXPERIMENTAL_TemplateListPageComponent: ArtaTemplateListPage,
          }}
        />
      }
    >
      <ScaffolderFieldExtensions>
        <AzureTokenFieldExtension />
        <MyGroupsPickerExtension />
        <AzureSubscriptionPickerExtension />
        <AzureResourceGroupPickerExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />}>
      <SettingsLayout>
        <SettingsLayout.Route path="general" title="General">
          <UserSettingsGeneral />
        </SettingsLayout.Route>
        <SettingsLayout.Route
          path="auth-providers"
          title="Authentication Providers"
        >
          <UserSettingsAuthProviders />
        </SettingsLayout.Route>
      </SettingsLayout>
    </Route>
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/template-management" element={<TemplateManagementPage />} />
    <Route path="/governance" element={<GovernancePage />} />
    <Route path="/services" element={<DeveloperAccessHubPage />} />
    <Route path="/aws-apps-search-page" element={<CatalogIndexPage />} />
    <Route
      path="/aws-apps-search-page/environments"
      element={<CatalogIndexPage />}
    />
    <Route
      path="/aws-apps-search-page/providers"
      element={<CatalogIndexPage />}
    />
    <Route path="/aws-apps-search-page/apps" element={<CatalogIndexPage />} />
    <Route
      path="/aws-apps-search-page/resources"
      element={<CatalogIndexPage />}
    />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
